import { Validators } from '@angular/forms'
import { InitiativeCategory } from '@common/enums/policy-initiative/initiative-category.enum'
import moment from 'moment'
import { InputType } from '../../../../abacus/enums/input-type.enum'
import { ResourceMode } from '../../../../abacus/enums/resource-mode.enum'
import { Field } from '../../../../abacus/interfaces/field.interface'
import { SelectOption } from '../../../../abacus/interfaces/select-option.interface'
import { BudgetAvailability } from '../../../../common/enums/policy-initiative/budget-availability.enum'
import { InitiativeMainCategory } from '../../../../common/enums/policy-initiative/initiative-category.enum'
import { InitiativeStatus } from '../../../../common/enums/policy-initiative/initiative-status.enum'
import { ResponsibleOrganisationLocation } from '../../../../common/enums/policy-initiative/responsible-organisation-location.enum'
import { ALL_CATEGORY_OPTIONS } from '../fields-definition/category-fields'
import { getStep1ConditionForm } from '../fields-definition/form-definition'
import { PolicyInitiativeCreateEditComponent } from '../policy-initiative-create-edit.component'
import {
  setCategoryField,
  showCategoryOptionField,
  syncBudgetFieldVisibility,
  updateInitiativeTypeOptions
} from './step-1-functions'

// Build the select options for the start year and end year fields.
const startYearFrom: number = 1900
const startYearTo: number = moment().add(5, 'year').year()
const endYearFrom: number = 2005
const endYearTo: number = moment().add(50, 'year').year()

export const getStep1Fields = (
  context: PolicyInitiativeCreateEditComponent
): Field[] => {
  return [
    {
      id: 'mainCategory',
      label: 'Select Initiative Scope',
      property: 'mainCategory',
      className: 'is-12',
      inputType: InputType.Radio,
      readonly: context.mode === ResourceMode.Edit,
      htmlTooltip:
        'Initiative scope cannot be changed when editing an existing policy initiative.',
      selectOptions: [
        {
          label: InitiativeMainCategory.Intergovernmental,
          value: InitiativeMainCategory.Intergovernmental,
          subLabel:
            'This includes AI initiatives from intergovernmental organisations (e.g. OECD, UN) and fora (e.g. G7, G20), as well as from supranational organisations (e.g. European Union, Arab League). Related governance bodies and mechanisms are also included.'
        },
        {
          label: InitiativeMainCategory.National,
          value: InitiativeMainCategory.National,
          subLabel:
            'This includes AI initiatives from countries or territories, as well as cross-border initiatives conducted jointly by multiple countries or territories'
        }
      ],
      required: true,
      onChange: async (newValue: InitiativeMainCategory) => {
        if (context.mode === ResourceMode.Edit) return

        const allFields = context.fields.filter(
          (field) => field.id !== 'mainCategory'
        )

        allFields.forEach((field) => {
          context.setFieldValue(field, null)
          field.hidden = true
        })
        const categoryField: Field = context.getFieldById('category')
        categoryField.selectOptions = structuredClone(
          ALL_CATEGORY_OPTIONS
        ).filter((option) => option.parent === newValue)
        context.setFieldValue(categoryField, '')

        await showCategoryOptionField(context)
      }
    },
    {
      id: 'category',
      label: 'Category',
      placeholder: 'Select Category...',
      property: 'category',
      className: 'is-12',
      hidden: true,
      inputType: InputType.Radio,
      required: true,
      selectOptions: structuredClone(ALL_CATEGORY_OPTIONS),
      onChange: async (newValue: InitiativeCategory) => {
        // Reset initiativeType when category changes
        const initiativeTypeField: Field =
          context.getFieldById('initiativeType')
        if (initiativeTypeField) {
          context.setFieldValue(initiativeTypeField, null)
          const formControl = context.form.get('initiativeTypeId')
          if (formControl) {
            formControl.setValue(null, { emitEvent: false })
            formControl.updateValueAndValidity()
          }
        }

        setCategoryField(context, newValue)
        await Promise.all([
          updateInitiativeTypeOptions(context, newValue),
          getStep1ConditionForm(newValue, context)
        ])
      }
    },
    {
      id: 'intergovernmentalOrganisation',
      label: 'Which organisation is responsible for the initiative?',
      property: 'intergovernmentalOrganisationId',
      className: 'is-12 tmp-is-background-success',
      inputType: InputType.Select,
      hidden: true,
      required: true,
      validators: [Validators.required],
      selectOptions: async () =>
        await context.componentResourceService.listSelectOptions(
          'intergovernmental-organisations',
          {
            orderBy: 'name'
          }
        )
    },

    {
      id: 'country',
      label: 'Which country/territory is the policy initiative linked to?',
      property: 'gaiinCountryId',
      className: 'is-12 tmp-is-background-multiple',
      inputType: InputType.Select,
      hidden: true,
      required: true,
      validators: [Validators.required],
      selectOptions: async () =>
        await context.componentResourceService.listSelectOptions('countries', {
          orderBy: 'name'
        })
    },
    {
      id: 'englishName',
      label: 'Name in english',
      helpText:
        'Please enter the name of the initiative in English, including the acronym if relevant',
      placeholder: 'Enter the name in English...',
      property: 'englishName',
      className: 'is-12',
      inputType: InputType.Text,
      required: true,
      hidden: true
    },
    {
      id: 'originalName',
      label: 'Name in original language',
      helpText:
        'If the name of the initiative is not originally in English, please enter the name in its original language',
      placeholder: 'Enter the name in original language...',
      property: 'originalName',
      className: 'is-12',
      inputType: InputType.Text,
      hidden: true,
      required: false
    },
    {
      id: 'sourceFiles',
      label: 'Official PDF',
      property: 'sourceFiles',
      helpText:
        'If your initiative has an official PDF document associated with it, please upload it here. You may upload versions in English as well as the original language.',
      className: 'is-12',
      inputType: InputType.MultiFile,
      askFileYear: true,
      hidden: true,
      placeholder: 'Choose a file to upload...'
    },
    {
      id: 'status',
      label: 'Status of initiative',
      property: 'status',
      className: 'is-12',
      inputType: InputType.Select,
      selectOptions: Object.entries(InitiativeStatus).map(([key, value]) => ({
        value: InitiativeStatus[key],
        label: value
      })),
      required: true,
      hidden: true,
      onChange: async (newValue: InitiativeStatus) => {
        const endYearFieldIndex = context.fields.findIndex(
          (field: Field) => field.id === 'endYear'
        )
        if (endYearFieldIndex !== -1) {
          const oldField = context.fields[endYearFieldIndex]
          const newField = {
            ...oldField,
            required: newValue === InitiativeStatus.Complete,
            validators:
              newValue === InitiativeStatus.Complete
                ? [Validators.required]
                : []
          }
          context.fields[endYearFieldIndex] = newField
          // Update the form control's validators
          const control = context.form.get(newField.property)
          if (control) {
            if (newValue === InitiativeStatus.Complete) {
              control.setValidators([Validators.required])
            } else {
              control.clearValidators()
              context.setFieldValue(newField, null)
            }
            control.updateValueAndValidity()
          }
          const resolvedFieldIndex = context.resolvedFields.findIndex(
            (field) => field.id === 'endYear'
          )
          if (resolvedFieldIndex !== -1) {
            context.resolvedFields[resolvedFieldIndex] = { ...newField }
          }
        }
        if (newValue === InitiativeStatus.Complete) {
          await context.showFields(['endYear'])
        } else {
          await context.hideAndEmptyFields(['endYear'])
        }
      }
    },
    {
      id: 'responsibleOrganisationSDLocation',
      label:
        'Who has primary responsibility for developing the national AI strategy?',
      className: 'is-12 tmp-is-background-warning',
      inputType: InputType.Select,
      selectOptions: Object.keys(ResponsibleOrganisationLocation).map(
        (key) => ({
          value: ResponsibleOrganisationLocation[key],
          label: ResponsibleOrganisationLocation[key]
        })
      ),
      property: 'responsibleOrganisationSDLocation',
      hidden: true,
      required: false
    },
    {
      id: 'responsibleOrganisation',
      label: 'Name of responsible organisation (in English)',
      property: 'responsibleOrganisation',
      className: 'is-12 tmp-is-background-multiple',
      inputType: InputType.Text,
      hidden: true,
      required: true
    },
    {
      id: 'responsibleOrganisationSILocation',
      label:
        'Who has primary responsibility for implementing the national strategy?',
      className: 'is-12 tmp-is-background-warning',
      inputType: InputType.Select,
      selectOptions: Object.keys(ResponsibleOrganisationLocation).map(
        (key) => ({
          value: ResponsibleOrganisationLocation[key],
          label: ResponsibleOrganisationLocation[key]
        })
      ),
      property: 'responsibleOrganisationSILocation',
      hidden: true,
      required: false
    },
    {
      id: 'responsibleOrganisationSI',
      label:
        'Name of responsible organisation for strategy implementation (in English)',
      className: 'is-12 tmp-is-background-warning',
      inputType: InputType.Text,
      property: 'responsibleOrganisationSI',
      hidden: true,
      required: false
    },
    {
      id: 'startYear',
      label: 'Start year',
      property: 'startYear',
      helpText:
        'Please enter the anticipated start date of Proposed initiatives, or the actual start date of Active and Complete initiatives.',
      className: 'is-12',
      inputType: InputType.Select,
      selectOptions: Array.from(
        { length: startYearTo - startYearFrom + 1 },
        (_, index) => startYearFrom + index
      ).map((year: number) => ({
        value: year,
        label: year.toString()
      })),
      hidden: true,
      required: true,
      onChange: (newValue: number) => {
        const endYearField = context.fields.find(
          (field) => field.id === 'endYear'
        )
        if (endYearField) {
          const startYear = Number(newValue)
          const maxYear = Number(endYearTo)
          endYearField.selectOptions = [
            { label: 'Not applicable', value: 0 }
          ].concat(
            Array.from(
              { length: maxYear - startYear + 1 },
              (_, index) => startYear + index
            ).map((year: number) => ({
              value: year,
              label: year.toString()
            }))
          )
        }
      }
    },
    {
      id: 'endYear',
      label: 'End year',
      property: 'endYear',
      helpText:
        'Please enter the anticipated end date of the initiative (if there is one), or if the initiative has already concluded, the actual end date.',
      className: 'is-12',
      inputType: InputType.Select,
      selectOptions: [{ label: 'Not applicable', value: 0 }].concat(
        Array.from(
          { length: endYearTo - endYearFrom + 1 },
          (_, index) => endYearFrom + index
        ).map((year: number) => ({
          value: year,
          label: year.toString()
        }))
      ),
      hidden: true,
      required: false
    },
    {
      id: 'initiativeType',
      label: 'Initiative Type',
      helpText:
        'Please indicate which type of initiative best fits the one you are entering.',
      className: 'is-12 tmp-is-background-multiple',
      inputType: InputType.GroupedSelect,
      selectOptions: [],
      property: 'initiativeTypeId',
      hidden: true,
      required: true,
      validators: [Validators.required]
    },
    {
      id: 'extentBinding',
      label: 'Extent binding',
      helpText:
        'Please select which of the items best matches the nature of the initiative.',
      className: 'is-12 tmp-is-background-multiple',
      inputType: InputType.Select,
      htmlTooltip:
        '<div><p><b>Legally binding:</b> Instruments such as treaties, conventions, and agreements that impose enforceable obligations on parties upon signature or ratification.</p>&nbsp;<p><b>Non-legally binding:</b> Instruments like declarations and guidelines that are not enforceable by law. These serve to guide behaviour, express intent, and share good practices without creating legal obligations.</p></div>',
      selectOptions: [
        {
          label: 'Binding',
          value: 'Binding'
        },
        {
          label: 'Non-binding',
          value: 'Non-binding'
        }
      ],
      property: 'extentBinding',
      hidden: true,
      required: false
    },
    {
      id: 'otherInitiativeType',
      label: 'Value-based initiative type',
      helpText:
        'If appropriate, please indicate an additional type of initiative that best fits the one you are entering',
      className: 'is-12 tmp-is-background-danger',
      hidden: true,
      property: 'otherInitiativeType',
      inputType: InputType.Text
    },
    {
      id: 'trustworthyAIRelation',
      label:
        'Do the indicated item(s) have any mandate/objectives related to trustworthy AI? If so, please specify',
      className: 'is-12 tmp-is-background-info',
      inputType: InputType.Textarea,
      property: 'trustworthyAIRelation',
      hidden: true
    },
    {
      id: 'actionPlan',
      label: 'Action plan and/or roadmap',
      helpText:
        'If this initiative has an action plan or roadmap, please add the hyperlink here.',
      className: 'is-12 tmp-is-background-multiple',
      inputType: InputType.Text,
      property: 'actionPlan',
      hidden: true
    },
    {
      id: 'budgetAvailable',
      property: 'budgetAvailable',
      label: 'Budget information available?',
      selectOptions: Object.entries(BudgetAvailability).map(([key, value]) => ({
        value: BudgetAvailability[key],
        label: value
      })),
      className: 'is-12 tmp-is-background-warning',
      inputType: InputType.Select,
      hidden: true,
      onChange: (): void => {
        syncBudgetFieldVisibility(context)
      }
    },
    {
      id: 'budget',
      label: 'Estimated budget expenditure range per year in Euros (EUR)',
      htmlHelpText: `<p>Please enter the estimated annual expenditure expected. For example, if a national AI strategy is budgeted for EUR 5 billion over the next five years, please enter the yearly figure of "1 000 000 000". You may wish to use a currency converter to convert your national currency into Euros. <a href="https://www.xe.com/currencyconverter" target="_blank">XE - Currency Converter</a></p>`,
      className: 'is-12 tmp-is-background-warning',
      inputType: InputType.Number,
      validators: [Validators.pattern('^[0-9]+$'), Validators.min(0)],
      property: 'budget',
      hidden: true
    },
    {
      id: 'engagementMechanism',
      label:
        'To what extent do you have mechanisms or other efforts to involve stakeholders in strategy design, implementation and monitoring?',
      helpText: 'Please select all that apply.',
      className: 'is-12 tmp-is-background-warning',
      inputType: InputType.MultiCheckbox,
      selectOptions: async (): Promise<SelectOption[]> =>
        await context.componentResourceService.listSelectOptions(
          'taxonomies/engagement-mechanisms',
          {
            orderBy: 'order'
          }
        ),
      property: 'engagementMechanismIds',
      hidden: true,
      required: false,
      onChange: async (newValue) => {
        const selectedValues = Array.isArray(newValue) ? newValue : []
        const hasPositiveSelection = selectedValues.some(
          (value) => value !== '2' && value !== '6'
        )
        if (hasPositiveSelection) {
          await context.showFields(['engagementDescription'])
        } else {
          await context.hideAndEmptyFields(['engagementDescription'])
        }
      }
    },
    {
      id: 'engagementDescription',
      label:
        'Please describe these mechanisms or other efforts for stakeholder engagement',
      className: 'is-12 tmp-is-background-warning',
      inputType: InputType.Textarea,
      max: 1000,
      property: 'engagementDescription',
      hidden: true
    },
    {
      id: 'intergovernmentalCoordination',
      label:
        'To what extent are there mechanisms that ensure inter-governmental coordination?',
      className: 'is-12 tmp-is-background-warning',
      inputType: InputType.Textarea,
      max: 500,
      property: 'intergovernmentalCoordination',
      hidden: true
    },
    {
      id: 'hasMonitoringMechanism',
      label: 'Is there a mechanism to monitor implementation?',
      className: 'is-12 tmp-is-background-multiple',
      inputType: InputType.Checkbox,
      property: 'hasMonitoringMechanism',
      hidden: true,
      onChange: (newValue: boolean): void => {
        newValue
          ? context.showFields(['monitoringMechanismDescription'])
          : context.hideAndResetFields(['monitoringMechanismDescription'])
      }
    },
    {
      id: 'monitoringMechanismDescription',
      label: 'Please describe the monitoring mechanism',
      className: 'is-12 tmp-is-background-warning',
      inputType: InputType.Text,
      property: 'monitoringMechanismDescription',
      hidden: true
    },
    {
      id: 'description',
      label: 'Short description',
      property: 'description',
      className: 'is-12',
      inputType: InputType.Textarea,
      htmlExample: `
      <p>This is an example of an HTML content that can be used to provide a short description of the initiative.</p>
      <p>It can include multiple paragraphs, lists, links, and other HTML elements.</p>
      `,
      max: 500,
      hidden: true,
      required: true
    },
    {
      id: 'overview',
      label: 'Overview',
      property: 'overview',
      htmlHelpText: `
      <div class="content is-small">
        <p>
          The initiative overview is an optional expanded overview of the initiative and its intended or demonstrated outcomes. This could potentially be the same text as an abstract, executive summary, fact sheet, or one-pager about the initiative.
      </p>
      <p>
          Going beyond the short and simple description, this should provide an expanded set of information for the initiative at a high level. In approximately 3-4 paragraphs (maximum 3,000 characters), please share additional information about the initiative, such as:
      </p>
      <ul>
          <li><strong>What</strong> issue does the initiative seek to resolve or what opportunity does it seek to leverage?</li>
          <li><strong>What</strong> are the objectives of the initiative?</li>
          <li><strong>How</strong> is the initiative expected to evolve in the future? For example, how will it be implemented and institutionalised? How could it scale more broadly?</li>
          <li><strong>How</strong> has the initiative <strong>evolved</strong> over time? (especially relevant for periodic updates)</li>
      </ul>
      </div>
      `,
      htmlExample: `
      <p>This is an example of an HTML content that can be used to provide an overview of the initiative.</p>
      <p>It can include multiple paragraphs, lists, links, and other HTML elements.</p>
      `,
      className: 'is-12',
      inputType: InputType.RichText,
      validators: [Validators.maxLength(3000)],
      max: 3000,
      hidden: true,
      required: false
    },
    {
      id: 'trustworthyAIMechanismDescription',
      label:
        'To what extent does the national AI strategy contain measures that enable the development and deployment of trustworthy AI?',
      className: 'is-12 tmp-is-background-warning',
      inputType: InputType.Textarea,
      property: 'trustworthyAIMechanismDescription',
      hidden: true,
      max: 500
    },
    {
      id: 'aiPrinciples',
      label: 'Relevant OECD AI Principle(s) addressed',
      htmlHelpText: `<p>The <a href="https://oecd.ai/en/ai-principles" target="_blank">OECD AI Principles</a> feature five value-based principles, and five recommendations for policymakers, as shown on the table below.
  Please select any principles that are relevant to this initiative (optional).</p>`,
      property: 'principleIds',
      className: 'is-12 tmp-is-background-multiple',
      inputType: InputType.MultiCheckbox,
      selectOptions: async (): Promise<SelectOption[]> =>
        await context.componentResourceService.listSelectOptions(
          'taxonomies/ai-principles',
          { orderBy: 'name' }
        ),
      hidden: true,
      required: false
      // validators: [Validators.required]
    },
    {
      id: 'targetSectors',
      label: 'Relevant sectors covered by this initiative',
      className: 'is-12',
      inputType: InputType.MultiSelect,
      selectOptions: async (): Promise<SelectOption[]> =>
        await context.componentResourceService.listSelectOptions(
          'taxonomies/target-sectors',
          { orderBy: 'name' }
        ),
      property: 'targetSectorIds',
      selectAllDisabled: true,
      placeholder: 'Select one or more relevant sectors...',
      hidden: true
    },
    {
      id: 'tags',
      label: 'AI tags',
      className: 'is-12',
      inputType: InputType.MultiSelect,
      selectOptions: async (): Promise<SelectOption[]> =>
        await context.componentResourceService.listSelectOptions(
          'taxonomies/ai-tags',
          { orderBy: 'name' }
        ),
      property: 'tagIds',
      selectAllDisabled: true,
      placeholder: 'Select one or more relevant tags...',
      hidden: true
    }
  ]
}
